import type {AiVerdict} from 'utils/aiScore';
import {makeAutoObservable, runInAction} from 'mobx';
import type {RootStore} from 'stores/rootStore';
import type {Workspace} from 'stores/layoutStore';
import type {Sentence} from 'types';
import type {HumanizerMode} from 'config';
import {
    HUMANIZER_MODES, HUMANIZER_TIP_TTL_MS, NOTICE_TTL_MS,
} from 'config';
import {
    AIScanData,
    HumanizerHistoryRow,
    ReportResponse,
    getHumanizerHistoryRequest,
    getReport,
    humanizeRequest,
    pollUploadReady,
    scanRequest,
    uploadFile,
    waitTaskResult,
    AI_TASK_RESULT_MAX_TRIES,
} from 'requests';
import type {HumanizerAttempt} from 'requests';
import {countWords, firstNWords} from 'utils/text';
import {checkError, getUUID, limitErrorToTrigger, reprNetworkError} from 'utils';
import type {ContinueStep, PaywallTrigger} from 'pages/ScanPage/IncreaseLimitsModal';
import {alignSentence} from 'utils/sentenceAlign';
import {logEvent} from 'analytics';
import {gtmFreeLimitReached, gtmHumanizer, type GtmPayload, type HumanizerSurface} from 'utils/gtm';
import {translate} from 'i18n/translator';
import {LocalizedText, resolveText} from 'i18n/text';
import {hasSeenHumanizerResultTip, markHumanizerResultTip} from 'persistence';
import {escapeHtml} from 'components/EditableTextarea/selection';
import {ForwardTextReveal} from 'lib/forwardTextReveal';
import {failureReason, isTransportFailure} from 'stores/toastStore';

export type HumanizerSide = 'input' | 'output';

/** A transient line under the input pane, with the way through when there is one. */
export interface HumanizerNotice {
    text: LocalizedText;
    action?: {label: LocalizedText; run: () => void};
    /** Shown until the next run or a new input text, not for a few seconds. */
    stays?: boolean;
}

/** One pane's AI verdict: the score, the text it was computed on, and its
 *  deep-scan segments. Whether the verdict is stale is derived — the pane's
 *  current text against `scannedText` — never tracked by event order. */
export interface SideScan {
    verdict?: AiVerdict;
    score: number | null;
    /** The exact text the score describes; null until a verdict has landed. */
    scannedText: string | null;
    segments: Sentence[];
    /** True once a segments lookup has completed, with or without a breakdown. */
    segmentsFetched: boolean;
    analysisUuid: string | null;
    loading: boolean;
}

const emptySide = (): SideScan =>
    ({score: null, scannedText: null, segments: [], segmentsFetched: false, analysisUuid: null, loading: false});

const toMode = (mode: string): HumanizerMode =>
    (HUMANIZER_MODES as readonly string[]).includes(mode) ? mode as HumanizerMode : 'medium';

// Consecutive keystrokes within this window collapse into one undo step
// (same rule as the grammar editor's history).
const TEXT_COALESCE_MS = 600;

// An hour of 500 ms polls.
export const HUMANIZER_POLL_TRIES = 7200;

const otherSide = (side: HumanizerSide): HumanizerSide => side === 'input' ? 'output' : 'input';

/**
 * A pane's HTML: a span per sentence, so hover can point at its counterpart in the other
 * pane and the counterpart can be marked. Sentences only when they reassemble the text
 * exactly — otherwise the pane must not silently show a different text than it holds.
 * The whitespace between sentences stays outside the spans: a hovered sentence must
 * not light up the gap after it.
 */
const renderPane = (text: string, segments: Sentence[]): string => {
    const sentences = segments.map(s => s.text ?? '');
    if (!sentences.length || sentences.join('') !== text) return escapeHtml(text);
    return sentences.map((sentence, i) => {
        const [, lead, body, trail] = /^(\s*)([\s\S]*?)(\s*)$/.exec(sentence)!;
        if (!body) return escapeHtml(sentence);
        return `${escapeHtml(lead)}<span class="humanizer__sentence" data-hsent="${i}">${escapeHtml(body)}</span>${escapeHtml(trail)}`;
    }).join('');
};

/**
 * The Humanizer: one run = POST /humanize (charged from the Humanizer wallet) +
 * two free deep AI scans the backend performs — of the original and of the
 * result. The store owns both panes' text, the two verdicts, the output pane's
 * undo/redo, the paid Re-scan of an edited pane, the run history and the
 * one-shot tag tooltips.
 */
export class HumanizerStore {
    root: RootStore;

    mode: HumanizerMode = 'medium';
    inputText = '';
    inputHtml = '';
    outputText = '';
    outputHtml = '';

    /** The humanize call itself (the result scan keeps polling after it's done). */
    isHumanizing = false;
    /** A dropped document is being uploaded and read into the input pane. */
    isLoadingFile = false;
    /** A humanized text is on screen — the output pane's empty state is gone. */
    hasResult = false;
    /** The original the rewrite on screen was made from; null until a rewrite has landed. */
    private humanizedFrom: string | null = null;

    input: SideScan = emptySide();
    output: SideScan = emptySide();

    /** Transient message under the input pane (too short / too long / run failed),
     *  with the way through when there is one ("Humanize first N words"). */
    notice: HumanizerNotice | null = null;
    private noticeTimer: ReturnType<typeof setTimeout> | null = null;

    /** The output's composition popover is owed its one-per-browser opening on its own. */
    firstPopoverDue = false;
    /** The once-per-session "text changed" hint, at whichever tag it belongs to. */
    textChangedTipSide: HumanizerSide | null = null;
    private textChangedShown = false;
    /** Bumped by every run, rescan and restore; async work compares against it
     *  and drops its result when the panes have moved on. */
    private generation = 0;
    private tipTimers: {changed?: ReturnType<typeof setTimeout>} = {};

    /** The sentences (by `data-hsent` index) lit in each pane: the one under the pointer
     *  (or tapped, on a phone) and what it maps to in the other pane. */
    marked: Record<HumanizerSide, number[]> = {input: [], output: []};
    /** The pane the marks came from — the other one is the one to scroll them into view in. */
    markedFrom: HumanizerSide | null = null;
    /** "side:index" → the counterpart's indices; the answer never changes between hovers. */
    private alignCache = new Map<string, number[]>();
    /** Reports already fetched for the composition popover / View Analysis, by uuid. */
    private reportCache = new Map<string, Promise<ReportResponse>>();

    // Output-pane undo/redo (text only — the pane holds plain text).
    private undoStack: string[] = [];
    private redoStack: string[] = [];
    private lastEditAt = 0;
    private runPollAbort: AbortController | null = null;
    private runStartedAt: number | null = null;
    private runAttempt: HumanizerAttempt | null = null;
    private runShare: {attempt: number; share: number} | null = null;
    private firstPreviewVisibleMs: number | null = null;
    private previewReveal = new ForwardTextReveal(text => runInAction(() => this.setOutputPlain(text)));

    get loadingStartedAt() {
        return this.isHumanizing ? this.runStartedAt : null;
    }

    /** Set from the second attempt on: the first one is the run itself, not a retry. */
    get retryAttempt() {
        const attempt = this.isHumanizing ? this.runAttempt : null;
        return attempt && attempt.number > 1 ? attempt : null;
    }

    /** How much of the rewrite has streamed in, 0–1; null until the server first tells. */
    get runProgress(): number | null {
        return this.isHumanizing ? this.runShare?.share ?? null : null;
    }

    /** Forward-only within an attempt; a retry starts over. Polls without a share (the attempt
     *  is validating, or Sol is on) hold the ring where it was rather than drop to the spinner. */
    private trackProgress(attempt: HumanizerAttempt | null) {
        const share = attempt?.progress;
        if (attempt == null || share == null) return;
        const sameAttempt = this.runShare?.attempt === attempt.number;
        this.runShare = {attempt: attempt.number, share: sameAttempt ? Math.max(this.runShare!.share, share) : share};
    }

    markPreviewVisible() {
        if (this.isHumanizing && this.hasText('output') && this.runStartedAt !== null && this.firstPreviewVisibleMs === null) {
            this.firstPreviewVisibleMs = performance.now() - this.runStartedAt;
        }
    }

    // History
    history: HumanizerHistoryRow[] = [];
    historyCount = 0;
    historyPage = 1;
    historyPageSize = 10;
    historyLoading = false;
    historyLoaded = false;

    /** The text last handed across between the scan editor and the input pane. */
    private sharedText: string | null = null;

    constructor(root: RootStore) {
        this.root = root;
        makeAutoObservable(this);
    }

    // ---- one draft across the workspaces ---------------------------------------------

    /** The input pane's text is the Humanizer's own once a verdict or a rewrite describes
     *  it, or a run is on it. */
    private get ownsText(): boolean {
        return this.hasResult || this.isHumanizing || this.input.score !== null || this.input.loading;
    }

    /** The scan editor's text is the scan's own once a scan holds a result or runs, a batch
     *  is staged, or a report is shown. */
    private get scanOwnsText(): boolean {
        const {input, batchFile, reportViewMode} = this.root;
        return reportViewMode || batchFile.isBatchMode || batchFile.isFilesMode
            || input.isAnyScanLoading || input.anyScanResultShown;
    }

    /**
     * A workspace is being entered: until something has been run the text is one draft,
     * whichever editor it was typed in, so the side entered takes the other one's text —
     * unless it owns its text, or holds a draft of its own: one the user changed since the
     * text was last handed across. An empty side always takes; an empty source never wipes.
     */
    syncDraft(entering: Workspace) {
        const input = this.root.input;
        const [from, to, owned] = entering === 'humanizer'
            ? [input.editorText, this.inputText, this.ownsText]
            : [this.inputText, input.text, this.scanOwnsText];
        if (owned || !from.trim() || from === to) return;
        if (to.trim() && to !== this.sharedText) return;
        if (entering === 'humanizer') this.setInputText(from);
        else input.setTextInPlace(from);
        this.sharedText = from;
    }

    // ---- text & mode ---------------------------------------------------------

    setMode(mode: HumanizerMode) {
        if (mode !== this.mode) void logEvent(gtmHumanizer('mode_change', {mode}));
        this.mode = mode;
    }

    /** Whether the pane's text no longer matches what its verdict was computed on. */
    isStale(side: HumanizerSide): boolean {
        return side === 'input' ? this.inputStale : this.outputStale;
    }

    textOf(side: HumanizerSide): string {
        return side === 'input' ? this.inputText : this.outputText;
    }

    /** The pane holds something to scan or humanize — whitespace alone does not count. */
    hasText(side: HumanizerSide): boolean {
        return side === 'input' ? !this.inputEmpty : !this.outputEmpty;
    }

    /** A rewrite is on screen: a run produced one and it has not been emptied since
     *  (an emptied pane shows its empty state; undo brings the text back). */
    get showsResult(): boolean {
        return this.hasResult && this.hasText('output');
    }

    /** Something of the Humanizer's is in flight — the run, or a verdict of either pane
     *  (the folder tab's ring, like a scan tab's). */
    get isBusy(): boolean {
        return this.isHumanizing || this.input.loading || this.output.loading;
    }

    // Computed per pane, like staleness: a keystroke wakes the readers only when
    // the answer flips, not on every character.
    get inputEmpty(): boolean {
        return this.inputText.trim().length === 0;
    }

    get outputEmpty(): boolean {
        return this.outputText.trim().length === 0;
    }

    get inputWords(): number {
        return countWords(this.inputText);
    }

    // Computed per pane, so a keystroke wakes the tag only when the answer flips.
    get inputStale(): boolean {
        return this.input.score !== null && !this.inputVerdictCurrent;
    }

    get outputStale(): boolean {
        const st = this.output;
        return st.score !== null && st.scannedText !== null && st.scannedText !== this.outputText;
    }

    /** User edit in the input pane. */
    onInputChange(html: string, text: string) {
        // Emptied, not edited: the rewrite and the verdicts describe a text that is gone,
        // and "text changed" is the wrong thing to say about a blank pane.
        if (!text.trim()) {
            // Already blank — nothing to tear down, and no generation to bump under a
            // document still being read into the pane.
            if (this.isBlank) return;
            return this.clearPanes();
        }
        const wasStale = this.isStale('input');
        this.inputHtml = html;
        this.inputText = text;
        this.dropStayingNotice();
        this.forgetAlignment();
        if (!wasStale && this.isStale('input')) this.announceTextChanged('input');
    }

    /** Nothing for `clearPanes` to empty: no input, no rewrite. */
    get isBlank(): boolean {
        return !this.inputText && !this.outputText && !this.hasResult;
    }

    pasteInput(text: string) {
        this.setInputText(text);
        void logEvent(gtmHumanizer('paste', {}));
    }

    /** Back to an empty Humanizer: both panes, both verdicts, the history of the rewrite.
     *  What clearing the input means, and what the toolbar's trash does. */
    clearPanes() {
        this.replacePanes('', '');
        this.hasResult = false;
        this.input = emptySide();
        this.output = emptySide();
        this.textChangedTipSide = null;
        this.forgetAlignment();
    }

    setInputText(text: string) {
        this.inputText = text;
        this.inputHtml = escapeHtml(text);
        this.dropStayingNotice();
        this.forgetAlignment();
    }

    /** A failure that stays is about the text it failed on: a new one makes it stale. */
    private dropStayingNotice() {
        if (this.notice?.stays) this.notice = null;
    }

    /** The panes no longer line up: drop the hover marks and what was resolved for them. */
    private forgetAlignment() {
        this.marked = {input: [], output: []};
        this.markedFrom = null;
        this.alignCache.clear();
    }

    /** The input's verdict describes the text on screen — nothing has been typed since. */
    get inputVerdictCurrent(): boolean {
        return this.input.score !== null && this.input.scannedText === this.inputText;
    }

    /** Whether Humanize would redo what is on screen: the rewrite shown is of this very text.
     *  A verdict of the text is not enough — a Re-scan of an edited original has one, and no rewrite. */
    get sameTextHumanized(): boolean {
        return this.hasResult && this.inputText === this.humanizedFrom;
    }

    /** User edit in the output pane: one coalesced undo step per typing burst. */
    onOutputChange(html: string, text: string) {
        if (this.isHumanizing) return;
        const now = Date.now();
        if (now - this.lastEditAt > TEXT_COALESCE_MS) {
            this.undoStack.push(this.outputText);
            this.redoStack = [];
        }
        this.lastEditAt = now;
        const wasStale = this.isStale('output');
        this.outputHtml = html;
        this.outputText = text;
        this.forgetAlignment();
        if (!wasStale && this.isStale('output')) this.announceTextChanged('output');
    }

    get canUndo() { return !this.isHumanizing && this.undoStack.length > 0; }
    get canRedo() { return !this.isHumanizing && this.redoStack.length > 0; }

    undoOutput = () => {
        if (this.isHumanizing) return;
        const prev = this.undoStack.pop();
        if (prev === undefined) return;
        void logEvent(gtmHumanizer('undo', {}));
        this.redoStack.push(this.outputText);
        this.stepOutputTo(prev);
    };

    redoOutput = () => {
        if (this.isHumanizing) return;
        const next = this.redoStack.pop();
        if (next === undefined) return;
        void logEvent(gtmHumanizer('redo', {}));
        this.undoStack.push(this.outputText);
        this.stepOutputTo(next);
    };

    /** A history step: the old marks no longer line up; back on the scanned text, the
     *  sentence spans come back with it. */
    private stepOutputTo(text: string) {
        this.setOutputPlain(text);
        this.forgetAlignment();
        this.landSegments('output', this.output.segments);
        this.lastEditAt = 0;
    }

    private setOutputPlain(text: string) {
        this.outputText = text;
        this.outputHtml = escapeHtml(text);
    }

    /** Redraw a pane from its text and its sentences. */
    private renderSide(side: HumanizerSide) {
        const html = renderPane(this.textOf(side), this[side].segments);
        if (side === 'input') this.inputHtml = html;
        else this.outputHtml = html;
    }

    // ---- notices & tag tooltips ------------------------------------------------

    /** Show a line under the input pane. It hides by itself, like useTransientNotice — unless
     *  it `stays`, as a failure does with its retry. */
    showNotice(text: LocalizedText, action?: HumanizerNotice['action'], {stays = false} = {}) {
        if (this.noticeTimer) clearTimeout(this.noticeTimer);
        this.noticeTimer = null;
        this.notice = {text, action, stays};
        if (!stays) this.noticeTimer = setTimeout(() => runInAction(() => { this.notice = null; }), NOTICE_TTL_MS);
    }

    private announceTextChanged(side: HumanizerSide) {
        // Once per session, at the tag whose verdict just went stale.
        if (this.textChangedShown) return;
        this.textChangedShown = true;
        this.textChangedTipSide = side;
        this.tipTimers.changed = setTimeout(
            () => runInAction(() => { this.textChangedTipSide = null; }), HUMANIZER_TIP_TTL_MS);
    }

    /** Hovering a tag dismisses whichever transient hint is pinned to it. */
    onTagHover(side: HumanizerSide) {
        if (this.textChangedTipSide === side) {
            if (this.tipTimers.changed) clearTimeout(this.tipTimers.changed);
            this.textChangedTipSide = null;
        }
    }

    /** The popover has opened on its own: spend the once-per-browser flag only now, so
     *  a verdict without an analysis (no popover to open) doesn't use it up. */
    consumeFirstPopover() {
        this.firstPopoverDue = false;
        markHumanizerResultTip();
    }

    // ---- the run ---------------------------------------------------------------

    /** Per-request input cap: the API's hard maximum, tightened by what the wallet
     *  still holds. null for an anon — their wall is the login, not a number. */
    get wordCap(): number | null {
        const user = this.root.user;
        if (user.isAnonymous) return null;
        return Math.min(user.humanizerRunCap ?? Infinity, user.remainingHumanizerWords);
    }

    /**
     * Whether `words` fit one run, and if not, what stands in the way — and what is
     * offered for it. Over the API's own ceiling no plan helps: a Free account still
     * gets the plans (the "too long" wall), a paid one only the first-N-words way
     * through. Over the wallet's remainder it is the wallet's wall, plans and all.
     * Returns the error code the server would have answered with, null when nothing stands.
     */
    private wall(words: number, entrypoint = 'humanizer_run'): string | null {
        const user = this.root.user;
        const runCap = user.humanizerRunCap;
        if (runCap !== null && words > runCap) {
            if (user.canStartSubscription) {
                this.openHumanizerPaywall(words, 'humanizer_words', entrypoint, true);
            } else {
                this.showNotice(() => translate('humanizer-error-too-long', {max: runCap.toLocaleString()}),
                    this.prefixAction(words));
            }
            return 'validation:humanizer_text_too_long';
        }
        if (words > user.remainingHumanizerWords) {
            this.openHumanizerPaywall(words, 'humanizer_words', entrypoint);
            return 'rich_limit:humanizer_words_limit';
        }
        return null;
    }

    /** The Humanize / Re-humanize button. `run` itself is also re-entered on its own
     *  (login resume, retry, paywall unblock), which must not count as a click. */
    runFromButton = () => {
        void logEvent(gtmHumanizer('run', {rerun: this.sameTextHumanized, mode: this.mode, words: this.inputWords}));
        void this.run(true);
    };

    /** `reportOutcome`: this run answers a click (Humanize, "Humanize first N words"), so it
     *  reports one run_success / run_error — none if it is abandoned (panes cleared, sign-in
     *  closed). Retry and the re-run after a purchase don't report. */
    run = async (reportOutcome = false) => {
        if (this.isHumanizing) return;
        const text = this.inputText;
        const words = this.inputWords;
        // The mode the request goes out with: the selector stays live during the run.
        const mode = this.mode;
        const user = this.root.user;

        if (user.isAnonymous) {
            // The words on offer are the account's — sign in first, and the run
            // continues by itself: in place via resume, or across an OAuth reload
            // via the draft the login writes (useAuth) and ScanPage restores.
            this.root.input.promptLogin('humanizer', {resume: () => this.run(reportOutcome)});
            return;
        }
        const floor = user.humanizerRunFloor;
        // A wall ends the click as the server's refusal would; the re-run it may offer is no click.
        const report = (outcome: GtmPayload) => { if (reportOutcome) void logEvent(outcome); };
        if (floor !== null && words < floor) {
            this.showNotice(() => translate('humanizer-error-too-short', {min: floor}));
            report(gtmHumanizer('run_error', {mode, words, error_code: 'validation:humanizer_text_too_short'}));
            return;
        }
        const walled = this.wall(words);
        if (walled) {
            report(gtmHumanizer('run_error', {mode, words, error_code: walled}));
            return;
        }

        // A verdict that already describes this exact text stays — the run is of the same
        // original, in another mode — and the backend is told not to scan it again.
        const sourceUuid = this.inputVerdictCurrent ? this.input.analysisUuid : null;
        const keepSource = !!sourceUuid;

        const gen = ++this.generation;
        this.previewReveal.start();
        this.runPollAbort?.abort();
        const pollAbort = new AbortController();
        this.runPollAbort = pollAbort;
        const startedAt = performance.now();
        this.runStartedAt = startedAt;
        this.runAttempt = null;
        this.runShare = null;
        this.firstPreviewVisibleMs = null;
        let firstPreviewMs: number | null = null;
        this.isHumanizing = true;
        this.hasResult = false;
        this.notice = null;
        if (!keepSource) this.input = {...emptySide(), loading: true};
        this.output = emptySide();
        this.setOutputPlain('');
        this.forgetAlignment();
        this.undoStack = [];
        this.redoStack = [];

        try {
            const resp = await humanizeRequest(text, mode, sourceUuid);
            if (gen !== this.generation) return;
            void user.refresh();
            // The original's free scan fills the left tag while the rewrite runs — unless
            // the verdict is already there and the backend agreed to reuse it (no task then).
            const originalVerdict = this.pollSide('input', gen, text, resp.ai_task_id ?? undefined, resp.analysis_uuid);

            // Sol streams the processed prefix into a shared preview. Short polls avoid holding a
            // web-server thread per user. The run's deadline grows with the text (about 50 min at
            // the longest) and the backend fails a stale run on the poll itself; the hour is a backstop.
            const task = await waitTaskResult(resp.humanizer_task_id, pollAbort.signal, HUMANIZER_POLL_TRIES, progress => {
                if (gen !== this.generation) return;
                const attempt: HumanizerAttempt | null = progress.humanizer_attempt ?? null;
                runInAction(() => {
                    if (attempt?.number !== this.runAttempt?.number || attempt?.max !== this.runAttempt?.max) this.runAttempt = attempt;
                    this.trackProgress(attempt);
                });
                // Older servers send replaceable whole-draft snapshots; don't
                // show those as a forward stream during a staggered rollout.
                if (progress.humanizer_preview?.kind !== 'prefix') return;
                const preview = progress.humanizer_preview.text;
                if (typeof preview !== 'string' || !preview.trim()) return;
                if (firstPreviewMs === null) firstPreviewMs = performance.now() - startedAt;
                this.previewReveal.append(preview);
            }, 500);
            if (gen !== this.generation) return;
            const data = task.data ?? {};
            const humanized: string = data.humanized_text ?? '';
            const resultReceivedMs = Math.round(performance.now() - startedAt);
            await this.previewReveal.finish(humanized);
            if (gen !== this.generation) return;
            runInAction(() => {
                this.isHumanizing = false;
                this.hasResult = true;
                this.humanizedFrom = text;
                this.setOutputPlain(humanized);
                this.output.loading = true;
            });
            report(gtmHumanizer('run_success', {mode, words, output_words: countWords(humanized)}));
            void logEvent(gtmHumanizer('result_timing', {
                task_id: resp.humanizer_task_id,
                first_preview_received_ms: firstPreviewMs === null ? null : Math.round(firstPreviewMs),
                first_preview_visible_ms: this.firstPreviewVisibleMs === null ? null : Math.round(this.firstPreviewVisibleMs),
                result_received_ms: resultReceivedMs,
                result_displayed_ms: Math.round(performance.now() - startedAt),
                reveal_tail_ms: Math.round(performance.now() - startedAt) - resultReceivedMs,
            }));
            const resultVerdict = this.pollSide('output', gen, humanized, data.result_ai_task_id, data.result_analysis_uuid);
            // The finished run is a history row, and its row carries both verdicts —
            // refresh the list once they have landed, not before.
            await Promise.allSettled([originalVerdict, resultVerdict]);
            if (gen === this.generation) void this.loadHistory({quiet: true});
        } catch (e: any) {
            if (gen !== this.generation) return;
            this.previewReveal.cancel();
            const code = reprNetworkError(e).code;
            report(gtmHumanizer('run_error', {mode, words, error_code: code}));
            runInAction(() => {
                this.isHumanizing = false;
                this.input.loading = false;
                this.setOutputPlain('');
                this.hasResult = false;
            });
            // Whatever failed, the hold the server took is released by now —
            // the wallet shown (and the cap it sets) must not stay charged for it.
            void user.refresh();
            if (this.root.input.isLimitError(code)) {
                this.openHumanizerPaywall(words, limitErrorToTrigger(code));
            } else if (checkError(code, 'humanizer_text_too_short')) {
                this.showNotice(() => translate('humanizer-error-too-short', {min: user.humanizerRunFloor ?? words}));
            } else if (checkError(code, 'humanizer_text_too_long')) {
                // The server's ceiling moved under a client that read an older one.
                this.showNotice(() => translate('humanizer-error-too-long', {max: (user.humanizerRunCap ?? words).toLocaleString()}));
            } else if (checkError(code, 'unsupported_language')) {
                this.showNotice(() => translate('scan-error-unsupported-language'));
            } else {
                // Why it failed decides what helps: the user's connection, or a moment on ours.
                const reason = failureReason(e);
                this.showNotice(() => translate('humanizer-error-failed', {reason: resolveText(reason)}),
                                {label: () => translate('humanizer-error-retry'), run: () => this.run()},
                                {stays: true});
            }
        }
    };

    /** Which wall the run hit: a text no allowance of this plan could ever take
     *  ("too long"), or an allowance that is simply spent ("used up"). */
    private walletTrigger(words: number): PaywallTrigger {
        return words > this.root.user.grantedHumanizerWords ? 'humanizer_words' : 'humanizer_quota';
    }

    /** The words pill was clicked: the wall the count is against — or, with nothing
     *  blocking, the plans (an anon gets the Humanizer's sign-in offer instead). */
    openWordCapPaywall() {
        void logEvent(gtmHumanizer('word_counter_click', {}));
        const user = this.root.user;
        if (user.isAnonymous) {
            this.root.input.promptLogin('humanizer');
            return;
        }
        if (this.wall(this.inputWords, 'humanizer_counter')) return;
        if (user.canStartSubscription) this.root.modal.showModal('increase-limits-form', {});
    }

    /**
     * The wallet's own refusal is titled by the wallet's state; any other limit keeps its
     * name — and a text over the run's ceiling is "too long" whatever the wallet holds.
     * Like the scan's paywall, it offers the free way through: humanize the prefix the
     * wallet still buys — when that prefix is a text worth humanizing at all.
     */
    private openHumanizerPaywall(words: number, trigger: PaywallTrigger = 'humanizer_words', entrypoint = 'humanizer_run', overRunCap = false) {
        if (trigger === 'humanizer_words' && !overRunCap) trigger = this.walletTrigger(words);
        logEvent(gtmFreeLimitReached({
            limit_type: 'humanizer_words',
            limit_value: String(this.root.user.remainingHumanizerWords),
            page_type: 'app_scan',
            entrypoint,
        }));
        const step = this.prefixStep(words, trigger);
        this.root.modal.showModal('increase-limits-form', {
            trigger,
            requirement: {humanizerWords: words},
            ...(step ? {continueSteps: [step]} : {}),
            onUnblocked: () => this.run(),
        });
    }

    /** "Humanize first N words" — null when the cap leaves nothing worth humanizing. */
    private prefixStep(words: number, trigger: PaywallTrigger): ContinueStep | null {
        const cap = this.prefixCap(words);
        return cap === null ? null : {kind: 'humanize_words', limit: cap, trigger, onApply: () => this.humanizePrefix(cap)};
    }

    /** The same offer as a notice's button, for the wall that comes without plans. */
    private prefixAction(words: number): HumanizerNotice['action'] {
        const cap = this.prefixCap(words);
        return cap === null ? undefined
            : {label: () => translate('paywall-cta-continue-humanize-words', {limit: cap.toLocaleString()}), run: () => this.humanizePrefix(cap)};
    }

    /** How many of `words` a run can take — null when that is nothing worth humanizing. */
    private prefixCap(words: number): number | null {
        const cap = this.wordCap;
        return cap === null || cap >= words || cap < (this.root.user.humanizerRunFloor ?? 0) ? null : cap;
    }

    private humanizePrefix(cap: number) {
        void logEvent(gtmHumanizer('humanize_first_words', {limit: cap}));
        this.setInputText(firstNWords(this.inputText, cap));
        void this.run(true);
    }

    /** Wait for a pane's AI verdict and land it. `scannedText` is the text the
     *  scan was dispatched with — what the verdict describes, whatever the pane
     *  holds by the time it lands. Resolves to whether a verdict landed. */
    private async pollSide(side: HumanizerSide, gen: number, scannedText: string,
                           taskId?: number, analysisUuid?: string): Promise<boolean> {
        if (!taskId) {
            runInAction(() => { this[side].loading = false; });
            return false;
        }
        this[side].loading = true;
        try {
            const task = await waitTaskResult(taskId, undefined, AI_TASK_RESULT_MAX_TRIES);
            if (gen !== this.generation) return false;
            const data = task.data ?? {};
            runInAction(() => {
                const mapped = this.root.aiScan.mapAIScanSegments(data.segmentation_data ?? []);
                this[side] = {
                    verdict: data.verdict ?? 'uncertain',
                    score: typeof data.score === 'number' ? data.score : null,
                    scannedText,
                    segments: mapped,
                    segmentsFetched: true,
                    analysisUuid: analysisUuid ?? null,
                    loading: false,
                };
                this.landSegments(side, mapped);
                if (side === 'output' && !hasSeenHumanizerResultTip()) this.firstPopoverDue = true;
            });
            return true;
        } catch (e) {
            // The previous verdict (if any) stays, still described by its own
            // scannedText — so a failed Re-scan keeps offering Re-scan, and a
            // failed free scan just leaves the tag empty without failing the run.
            if (gen === this.generation) runInAction(() => { this[side].loading = false; });
            return false;
        }
    }

    // ---- the paid Re-scan of an edited pane -------------------------------------

    /** The Re-scan button; `rescan` is also re-entered when its paywall unblocks. */
    rescanFromButton = (side: HumanizerSide) => {
        void logEvent(gtmHumanizer('rescan', {side}));
        void this.rescan(side);
    };

    rescan = async (side: HumanizerSide) => {
        const st = this[side];
        if (st.loading || this.isHumanizing || !this.hasText(side)) return;
        const text = this.textOf(side);
        const words = countWords(text);
        const input = this.root.input;

        // The same walls a normal scan meets, checked up front so the paywall can
        // name the limit instead of relaying a 403. affordableWords owns the
        // per-word pricing rule (a deep AI scan is ×1 today).
        if (input.isDeepScanLimitReached()) {
            this.root.modal.showModal('increase-limits-form', {trigger: 'deep_scan_limit'});
            return;
        }
        if (words > input.affordableWords({ai: true, deep: true, plagiarism: false, grammar: false})) {
            this.root.modal.showModal('increase-limits-form', {
                trigger: 'words_limit_reached',
                requirement: {words},
                onUnblocked: () => this.rescan(side),
            });
            return;
        }

        // The panes stay: a rescan rides the current generation and is dropped only by
        // what replaces them (a run, a restore). Bumping it here stranded the other
        // pane's rescan mid-flight — its verdict never landed and its loading never ended.
        const gen = this.generation;
        st.loading = true;
        try {
            // ai_check + deep_scan: deep is a modifier of the AI scan, not a type of its own.
            const resp = await scanRequest(text, null, true, true, false, false, 'humanizer_rescan');
            if (gen !== this.generation) return;
            void this.root.user.refresh();
            const landed = await this.pollSide(side, gen, text, resp.ai_task_id, resp.analysis_uuid);
            if (!landed && gen === this.generation) this.showNotice(() => translate('humanizer-error-rescan-failed'));
        } catch (e: any) {
            if (gen !== this.generation) return;
            const code = reprNetworkError(e).code;
            runInAction(() => { st.loading = false; });
            void this.root.user.refresh();
            if (input.isLimitError(code)) {
                this.root.modal.showModal('increase-limits-form', {
                    trigger: limitErrorToTrigger(code),
                    requirement: {words},
                    onUnblocked: () => this.rescan(side),
                });
            } else {
                this.showNotice(() => translate('humanizer-error-rescan-failed'));
            }
        }
    };

    // ---- composition popover / View Analysis ------------------------------------

    /** One report fetch per analysis: the popover and View Analysis share it, and
     *  a hover-out-hover cannot fire two parallel requests. */
    private fetchReport(uuid: string): Promise<ReportResponse> {
        let promise = this.reportCache.get(uuid);
        if (!promise) {
            promise = getReport(uuid).catch(e => {
                this.reportCache.delete(uuid);
                throw e;
            });
            this.reportCache.set(uuid, promise);
        }
        return promise;
    }

    /** A restored history row carries scores but no segments — fetch its report
     *  once the composition popover actually needs them. */
    ensureSegments = async (side: HumanizerSide) => {
        const st = this[side];
        if (st.segmentsFetched || !st.analysisUuid || st.loading) return;
        const gen = this.generation;
        let segments: Sentence[] = [];
        let score = st.score;
        let verdict = st.verdict;
        try {
            const resp = await this.fetchReport(st.analysisUuid);
            const aiTask = resp.items.find(item => item.type === 'ai')?.data as AIScanData | undefined;
            segments = this.root.aiScan.mapAIScanSegments(aiTask?.segmentation_data ?? []);
            verdict = aiTask?.verdict ?? 'uncertain';
            if (typeof aiTask?.score === 'number') score = aiTask.score;
        } catch (_) { /* no breakdown, and the popover says so instead of spinning */ }
        if (gen !== this.generation || this[side] !== st) return;
        runInAction(() => {
            st.score = score;
            st.verdict = verdict;
            st.segments = segments;
            st.segmentsFetched = true;
            this.landSegments(side, segments);
        });
    };

    /** A breakdown has arrived for a pane: its sentences become the spans hover maps to the
     *  other pane from — as long as the pane still shows the text they describe. */
    private landSegments(side: HumanizerSide, segments: Sentence[]) {
        if (!segments.length || this.textOf(side) !== this[side].scannedText) return;
        this.forgetAlignment();
        this.renderSide(side);
    }

    /** Open this pane's analysis in the AI scan tab, replacing whatever it held — after
     *  a confirm when that is a text of its own, or files, staged or scanned (Figma
     *  `Replace current text?`). Until this click the two workspaces are deliberately
     *  independent. */
    viewAnalysis = async (side: HumanizerSide) => {
        const uuid = this[side].analysisUuid;
        if (!uuid) return;
        void logEvent(gtmHumanizer('view_full_analysis', {side}));
        const {input, aiScan, layout} = this.root;
        // The tab still shows this analysis (the text came over by Humanize text): it is
        // there to go back to as it is — the batch and the other checks' results included.
        if (uuid === aiScan.analysisUuid) {
            layout.setWorkspace('scan', {back: true});
            input.setActiveSection('AI');
            return;
        }
        let resp: ReportResponse;
        try {
            resp = await this.fetchReport(uuid);
        } catch (e) {
            this.root.toast.error('humanizer-view-analysis-error', e);
            return;
        }
        const aiTask = resp.items.find(item => item.type === 'ai')?.data as AIScanData | undefined;
        if (!aiTask) return;
        const text = aiTask.text ?? '';
        if (!await input.confirmReplace(text, 'humanizer-replace-body', input.analysisSections(resp.items))) return;
        runInAction(() => {
            // Opens on the AI tab: the analysis has a verdict.
            input.openAnalysis({uuid, date: resp.date, text, items: resp.items});
            // Leaving through Back is the intuitive way home from the analysis.
            layout.setWorkspace('scan', {back: true});
        });
    };

    // ---- sentence hover → the pair lights up in both panes --------------------------

    /** A sentence of `side` is hovered or tapped (null: left): mark it and what it maps
     *  to in the other pane. */
    hoverSentence(side: HumanizerSide, index: number | null) {
        const own = index === null ? [] : [index];
        const counterpart = index === null ? [] : this.resolveCounterpart(side, index);
        // Every sentence-boundary crossing fires a null between two hovers: a mark that
        // would not change is not re-set, so the panes are not re-marked for nothing.
        const same = (a: number[], b: number[]) => a.length === b.length && a.every((i, k) => i === b[k]);
        if (same(own, this.marked[side]) && same(counterpart, this.marked[otherSide(side)])) return;
        this.marked = {...this.marked, [side]: own, [otherSide(side)]: counterpart};
        this.markedFrom = index === null ? null : side;
    }

    private resolveCounterpart(side: HumanizerSide, index: number): number[] {
        const key = `${side}:${index}`;
        const cached = this.alignCache.get(key);
        if (cached) return cached;
        const from = this[side].segments;
        const to = this[otherSide(side)].segments;
        // By the words the sentences share; a sentence sharing none (a short one, a
        // rewrite from scratch) falls back to its position when the counts line up.
        let found = alignSentence(from[index]?.text ?? '', to.map(s => s.text ?? ''));
        if (!found.length && to.length === from.length && to[index]?.text?.trim()) found = [index];
        this.alignCache.set(key, found);
        return found;
    }

    // ---- history -----------------------------------------------------------------

    /** True once the list matches the server — the section can skip refetching on remount. */

    /** A `quiet` load — the refresh after a run, with no one asking for the list — fails
     *  unseen; any other is told in a toast. The list keeps its rows either way. */
    async loadHistory({quiet = false}: {quiet?: boolean} = {}) {
        if (!this.root.user.user?.history_retention) return;
        this.historyLoading = true;
        try {
            const data = await getHumanizerHistoryRequest(this.historyPage, this.historyPageSize);
            runInAction(() => {
                this.history = data?.results ?? [];
                this.historyCount = data?.count ?? 0;
                this.historyLoaded = true;
            });
        } catch (e) {
            if (!quiet) this.root.toast.error('history-load-error', e);
        } finally {
            runInAction(() => { this.historyLoading = false; });
        }
    }

    setHistoryPage(page: number) {
        this.historyPage = page;
        void this.loadHistory();
    }

    setHistoryPageSize(size: number) {
        this.historyPageSize = size;
        this.historyPage = 1;
        void this.loadHistory();
    }

    /** Put a past run back on the two panes exactly as it finished — a failed run
     *  brings back its original and mode, ready to run again, and nothing else. */
    restore(row: HumanizerHistoryRow, surface: HumanizerSurface = 'scan') {
        const failed = row.humanized_text === null;
        void logEvent(gtmHumanizer('history_restore', {failed}, surface));
        this.replacePanes(row.original_text, row.humanized_text ?? '');
        this.mode = toMode(row.mode);
        this.hasResult = !failed;
        this.humanizedFrom = failed ? null : row.original_text;
        this.input = {
            ...emptySide(), score: row.original_ai_score, scannedText: row.original_text,
            analysisUuid: row.original_analysis_uuid,
        };
        this.output = failed ? emptySide() : {
            ...emptySide(), score: row.humanized_ai_score, scannedText: row.humanized_text,
            analysisUuid: row.result_analysis_uuid,
        };
        // Everything the run had comes back: the breakdowns behind the two verdicts
        // (the composition popovers, sentence hover) are fetched, not left for a hover.
        void this.ensureSegments('input');
        if (!failed) void this.ensureSegments('output');
    }

    /** The AI tab's result comes over (its "Humanize text"): the text goes on the input pane
     *  with the verdict that describes it, so the tag shows the score at once and the run
     *  names the analysis instead of scanning the text again (`run`'s sourceUuid). Whatever
     *  the panes held belongs to the run being replaced. */
    adoptScan(text: string, verdict: Pick<SideScan, 'score' | 'segments' | 'analysisUuid' | 'verdict'>) {
        this.replacePanes(text, '');
        this.hasResult = false;
        this.input = {...verdict, scannedText: text, segmentsFetched: true, loading: false};
        this.output = emptySide();
        this.landSegments('input', verdict.segments);
    }

    /** Both panes get new text; whatever was running belongs to the panes being replaced. */
    private replacePanes(inputText: string, outputText: string) {
        this.previewReveal.cancel();
        this.runPollAbort?.abort();
        this.generation++;
        this.setInputText(inputText);
        this.setOutputPlain(outputText);
        this.humanizedFrom = null;
        this.isHumanizing = false;
        this.notice = null;
        this.undoStack = [];
        this.redoStack = [];
    }

    // ---- a document into the input pane ---------------------------------------------

    /** Upload a document and put its text on the input pane — the same extraction
     *  the scan's file upload runs, the text is all the Humanizer keeps of it. */
    loadFile = async (file: File) => {
        if (this.isLoadingFile) return;
        this.isLoadingFile = true;
        const gen = this.generation;
        let failed = true;
        try {
            let ready = await uploadFile(file, getUUID());
            if ('error' in ready) throw new Error(ready.error);
            if (ready.status === 'processing') {
                ready = await pollUploadReady(ready.uuid, ready, {isAlive: () => gen === this.generation});
            }
            const text = ready.status === 'failed' ? '' : (ready.text ?? '').trim();
            if (!text) throw new Error('empty document');
            if (gen !== this.generation) return;
            this.setInputText(text);
            failed = false;
        } catch (e) {
            if (gen !== this.generation) return;
            if (isTransportFailure(e)) this.root.toast.error('humanizer-file-upload-error', e);
            else this.showNotice(() => translate('humanizer-file-failed'));
        } finally {
            runInAction(() => { this.isLoadingFile = false; });
        }
        void logEvent(gtmHumanizer('file_upload', {failed}));
    };

    /** Continue the run an anon started before login (see persistence.consumeHumanizerDraft). */
    restoreDraft(draft: {text: string, mode: string, autoRun: boolean}) {
        this.mode = toMode(draft.mode);
        this.setInputText(draft.text);
        // The click that started this run was counted before the login reloaded the page.
        if (draft.autoRun) void this.run(true);
    }
}
